import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Unit, unitService } from '../services/unitService';
import { Project, projectService } from '../services/projectService';
import '../css/standard-components.css';

const AddUnit: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const projectId = searchParams.get('projectId');
  const { user } = useAuth();
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [formData, setFormData] = useState({
    projectId: parseInt(projectId || '0') || 0,
    unitType: '',
    unitNo: '',
    wing: '',
    size: '',
    currentStatus: 'unsold',
    terraceStatus: '',
    terraceSize: '',
    saleDeedAmount: undefined as number | undefined,
    extraWorkAmount: undefined as number | undefined,
    extraSpace: '',
    extraSpaceAmount: undefined as number | undefined
  });

  useEffect(() => {
    const path = window.location.pathname;
    const isViewMode = path.includes('/view');
    const isEditMode = path.includes('/edit');
    
    setIsView(isViewMode);
    setIsEdit(isEditMode);

    loadProjects();

    if (id && (isEditMode || isViewMode)) {
      loadUnit();
    }
  }, [id]);

  const loadProjects = async () => {
    try {
      const response = await projectService.getProjects();
      setProjects(response);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadUnit = async () => {
    try {
      setLoading(true);
      console.log('📍 Loading unit with ID:', id);
      const unit = await unitService.getUnit(parseInt(id!));
      console.log('✅ Unit loaded:', unit);
      setFormData({
        projectId: unit.projectId,
        unitType: unit.unitType || '',
        unitNo: unit.unitNo || '',
        wing: unit.wing || '',
        size: unit.size || '',
        currentStatus: unit.currentStatus || 'unsold',
        terraceStatus: unit.terraceStatus || '',
        terraceSize: unit.terraceSize || '',
        saleDeedAmount: unit.saleDeedAmount,
        extraWorkAmount: unit.extraWorkAmount,
        extraSpace: unit.extraSpace || '',
        extraSpaceAmount: unit.extraSpaceAmount
      });
      console.log('✅ Form data updated');
    } catch (error) {
      console.error('❌ Error loading unit:', error);
      alert('Error loading unit');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'projectId' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isView) return;

    try {
      setLoading(true);
      
      if (isEdit) {
        const { projectId: _, ...updateData } = formData;
        await unitService.updateUnit(parseInt(id!), updateData);
        alert('Unit updated successfully!');
      } else {
        const { projectId, ...createData } = formData;
        await unitService.createUnit(projectId, createData);
        alert('Unit created successfully!');
      }
      
      if (projectId) {
        navigate(`/project/units?projectId=${projectId}`);
      } else {
        navigate('/units');
      }
    } catch (error) {
      console.error('Error saving unit:', error);
      alert('Error saving unit');
    } finally {
      setLoading(false);
    }
  };

  const getPageTitle = () => {
    if (isView) return 'View Unit';
    if (isEdit) return 'Edit Unit';
    return 'Add Unit';
  };

  const getBackUrl = () => {
    if (projectId) {
      return `/project/units?projectId=${projectId}`;
    }
    return '/units';
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{getPageTitle()}</h1>
        <button 
          className="btn btn-secondary"
          onClick={() => navigate(getBackUrl())}
        >
          Back to Units
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="standard-form">
          <div className="form-grid">
            {!projectId && (
              <div className="form-group">
                <label htmlFor="projectId">Project *</label>
                <select
                  id="projectId"
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleInputChange}
                  required
                  disabled={isView}
                  className="form-select"
                >
                  <option value="">Select Project</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="unitType">Unit Type</label>
              <select
                id="unitType"
                name="unitType"
                value={formData.unitType}
                onChange={handleInputChange}
                disabled={isView}
                className="form-select"
              >
                <option value="">Select Unit Type</option>
                <option value="SHOP">SHOP</option>
                <option value="Shop">Shop</option>
                <option value="1BHK">1BHK</option>
                <option value="2BHK">2BHK</option>
                <option value="3BHK">3BHK</option>
                <option value="3 Bhk">3 Bhk</option>
                <option value="4BHK">4BHK</option>
                <option value="4 Bhk">4 Bhk</option>
                <option value="5BHK">5BHK</option>
                <option value="PENTHOUSE">PENTHOUSE</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="wing">Wing</label>
              <input
                type="text"
                id="wing"
                name="wing"
                value={formData.wing}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                placeholder="Optional"
              />
            </div>

            <div className="form-group">
              <label htmlFor="unitNo">Unit Number *</label>
              <input
                type="text"
                id="unitNo"
                name="unitNo"
                value={formData.unitNo}
                onChange={handleInputChange}
                required
                disabled={isView}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="size">Size</label>
              <input
                type="text"
                id="size"
                name="size"
                value={formData.size}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                placeholder="e.g. 330 Sq Yards"
              />
            </div>

            <div className="form-group">
              <label htmlFor="currentStatus">Status</label>
              <select
                id="currentStatus"
                name="currentStatus"
                value={formData.currentStatus}
                onChange={handleInputChange}
                disabled={isView}
                className="form-select"
              >
                <option value="unsold">Unsold</option>
                <option value="sold">Sold</option>
                <option value="approval_pending">Approval Pending</option>
                <option value="hold">Hold</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="saleDeedAmount">Sale Deed Amount</label>
              <input
                type="number"
                id="saleDeedAmount"
                name="saleDeedAmount"
                value={formData.saleDeedAmount || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  saleDeedAmount: e.target.value ? Number(e.target.value) : undefined
                })}
                onWheel={(e) => e.currentTarget.blur()}
                disabled={isView}
                className="form-input"
                placeholder="Enter amount"
              />
            </div>

            <div className="form-group">
              <label htmlFor="extraWorkAmount">Extra Work Amount</label>
              <input
                type="number"
                id="extraWorkAmount"
                name="extraWorkAmount"
                value={formData.extraWorkAmount || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  extraWorkAmount: e.target.value ? Number(e.target.value) : undefined
                })}
                onWheel={(e) => e.currentTarget.blur()}
                disabled={isView}
                className="form-input"
                placeholder="Enter amount"
              />
            </div>

            <div className="form-group">
              <label htmlFor="terraceStatus">Terrace Status</label>
              <select
                id="terraceStatus"
                name="terraceStatus"
                value={formData.terraceStatus}
                onChange={handleInputChange}
                disabled={isView}
                className="form-select"
              >
                <option value="">No Terrace</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="terraceSize">Terrace Size</label>
              <input
                type="text"
                id="terraceSize"
                name="terraceSize"
                value={formData.terraceSize}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                placeholder="e.g. 100 Sq Ft"
              />
            </div>

            <div className="form-group">
              <label htmlFor="extraSpace">Extra Space</label>
              <input
                type="text"
                id="extraSpace"
                name="extraSpace"
                value={formData.extraSpace}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                placeholder="Parking, Storage, etc."
              />
            </div>

            <div className="form-group">
              <label htmlFor="extraSpaceAmount">Extra Space Amount</label>
              <input
                type="number"
                id="extraSpaceAmount"
                name="extraSpaceAmount"
                value={formData.extraSpaceAmount || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  extraSpaceAmount: e.target.value ? Number(e.target.value) : undefined
                })}
                onWheel={(e) => e.currentTarget.blur()}
                disabled={isView}
                className="form-input"
                placeholder="Enter amount"
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate(getBackUrl())}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            {!isView && (
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? 'Saving...' : (isEdit ? 'Update Unit' : 'Create Unit')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUnit;
